'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { fetchAllPaddles, buildCategories_list, type Paddle, type CategoryInfo } from '@/lib/services/paddleService';

import {
  BoltIcon,
  AdjustmentsHorizontalIcon,
  Square3Stack3DIcon,
  ShieldCheckIcon,
  ArrowsPointingOutIcon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline';

const categoryIconMap: Record<string, React.ReactNode> = {
  power: <BoltIcon className="category-hero-icon" />,
  control: <AdjustmentsHorizontalIcon className="category-hero-icon" />,
  'all-court': <Square3Stack3DIcon className="category-hero-icon" />,
  tournament: <ShieldCheckIcon className="category-hero-icon" />,
  elongated: <ArrowsPointingOutIcon className="category-hero-icon" />,
  widebody: <ViewColumnsIcon className="category-hero-icon" />,
};

export default function CategoriesPage() {
  const [paddles, setPaddles] = useState<Paddle[]>([]);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllPaddles().then(data => {
      setPaddles(data);
      setCategories(buildCategories_list(data));
      setLoading(false);
    });
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#EBEBEB' }}>
      <Navigation />

      {/* Header */}
      <section
        className="pt-32 pb-18 px-4 relative overflow-hidden"
        style={{ background: '#FFFFFF', paddingBottom: '5rem' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse at 75% -5%, rgba(217,0,18,0.04) 0%, transparent 55%)',
        }} />
        <div className="max-w-container mx-auto relative z-10">
          <div className="breadcrumb mb-6">
            <Link href="/" style={{ textDecoration: 'none' }}>Home</Link>
            <span className="breadcrumb-sep">/</span>
            <span style={{ color: 'rgba(10,10,10,0.55)' }}>Categories</span>
          </div>
          <div className="premium-chip accent mb-6">Browse All</div>
          <h1
            className="font-black mb-4"
            style={{
              fontSize: 'clamp(32px, 5vw, 58px)',
              letterSpacing: '-0.048em',
              color: '#0A0A0A',
              lineHeight: 1.02,
            }}
          >All Categories</h1>
          <p style={{ color: 'rgba(10,10,10,0.44)', fontSize: '16px', lineHeight: 1.75 }}>
            Browse paddles by type, material, and skill level
          </p>
        </div>
      </section>

      <div className="max-w-container mx-auto px-4">
        <div className="premium-divider" />
      </div>

      <main className="max-w-container mx-auto px-4 py-18" style={{ paddingTop: '4.5rem', paddingBottom: '4.5rem' }}>
        {/* Category grid */}
        <section className="mb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories?.map(cat => (
              <Link
                key={cat?.slug}
                href={`/categories/${cat?.slug}`}
                className="category-card group"
              >
                {/* Icon block */}
                <div className="category-card-icon-wrap">
                  <div className="category-card-icon-bg">
                    {categoryIconMap[cat?.slug] ?? <BoltIcon className="category-hero-icon" />}
                  </div>
                  <div className="category-card-count-pill">
                    {cat?.count}
                  </div>
                </div>

                {/* Text block */}
                <div className="category-card-body">
                  <h2 className="category-card-title">{cat?.name}</h2>
                  <p className="category-card-desc">{cat?.description}</p>
                  <div className="category-card-footer">
                    <span className="category-card-count-label">{cat?.count} paddles</span>
                    <span className="category-card-cta">
                      Explore
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'inline-block', marginLeft: '5px', transition: 'transform 0.2s ease', verticalAlign: 'middle' }}>
                        <path d="M2.5 7H11.5M7.5 3L11.5 7L7.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* All paddles */}
        <section>
          <div className="flex items-center justify-between mb-9">
            <h2 className="section-heading">All Paddles</h2>
            <Link
              href="/compare"
              className="text-xs font-bold uppercase tracking-widest transition-colors"
              style={{ color: 'rgba(10,10,10,0.36)', letterSpacing: '0.12em', textDecoration: 'none', transitionDuration: '0.2s' }}
              onMouseEnter={e => ((e.currentTarget as HTMLElement).style.color = '#0A0A0A')}
              onMouseLeave={e => ((e.currentTarget as HTMLElement).style.color = 'rgba(10,10,10,0.36)')}
            >Compare any paddle →</Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div style={{ color: 'rgba(10,10,10,0.4)', fontSize: '14px' }}>Loading paddles…</div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5">
              {paddles?.map((paddle, i) => (
                <Link
                  key={paddle?.id}
                  href={`/paddles/${paddle?.slug}`}
                  className="rounded-2xl overflow-hidden"
                  style={{
                    background: '#FFFFFF',
                    border: '1.5px solid rgba(0,0,0,0.08)',
                    boxShadow: 'var(--shadow-card)',
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'transform var(--transition), box-shadow var(--transition), border-color var(--transition)',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(217,0,18,0.2)';
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(-5px)';
                    (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-card-hover)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,0,0,0.08)';
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                    (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-card)';
                  }}
                >
                  <div className="relative" style={{ paddingBottom: '100%', background: 'linear-gradient(145deg, #F6F6F6 0%, #ECECEC 100%)' }}>
                    <div
                      className="absolute top-2 left-2 z-10 w-6 h-6 rounded-full flex items-center justify-center"
                      style={{
                        background: 'rgba(255,255,255,0.96)',
                        color: 'rgba(10,10,10,0.42)',
                        fontSize: '9px',
                        fontWeight: 800,
                        border: '1.5px solid rgba(0,0,0,0.08)',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                      }}
                    >
                      {i + 1}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <img
                        src={paddle?.image}
                        alt={paddle?.imageAlt}
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }}
                      />
                    </div>
                  </div>
                  <div className="p-3.5">
                    <div
                      className="text-xs font-bold leading-tight"
                      style={{ color: '#0A0A0A', letterSpacing: '-0.015em' }}
                    >{paddle?.name}</div>
                    <div className="text-xs mt-1.5 font-black tabular-nums" style={{ color: '#D90012' }}>{paddle?.priceFormatted}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
